// Goals tab (Goals & Coach spec, Phase 1): Setup, Goals, Metrics & milestones, Coach.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Target, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { GoalBars } from '../components/goals/GoalBlocks';
import { fmt, SetupWizard } from '../components/goals/SetupWizard';
import { Button, Card, cx, Empty, ErrorBox, Field, Input, PageHeader, Pill, Select, Spinner } from '../components/ui';
import { useUserId } from '../lib/auth';
import { date, money, todayISO } from '../lib/format';
import { actualFor, emptyAnswers, emptyUrgency, EXPERIENCE_LABEL, useActuals, useGoals } from '../lib/goals/data';
import { METRIC_LABEL, METRIC_NOUN, ROLE_TEMPLATES, type Metric } from '../lib/goals/engine';
import { supabase, unwrap } from '../lib/supabase';

const TABS = [
  { key: 'setup', label: 'Setup' },
  { key: 'goals', label: 'Goals' },
  { key: 'metrics', label: 'Metrics & milestones' },
  { key: 'coach', label: 'Coach' },
] as const;

export function Goals() {
  const { data: g, isLoading, error } = useGoals();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'goals';
  const [wizard, setWizard] = useState<number | null>(null);

  if (isLoading) return <Spinner />;
  if (error || !g) return <ErrorBox error={error} />;
  const p = g.profile;

  if (wizard !== null || !p) {
    return (
      <div>
        <PageHeader title="Goals" subtitle="Set a realistic income goal; the app works backwards to the activity it takes." />
        {wizard === null ? (
          <Card>
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <Target className="h-10 w-10 text-indigo-500" />
              <p className="max-w-md text-sm text-slate-600">Answer a few questions (about 5 minutes) and you’ll get weekly targets for calls and meetings, milestones, and a coach to keep you on pace.</p>
              <Button variant="primary" onClick={() => setWizard(0)}>
                Set up your goals
              </Button>
            </div>
          </Card>
        ) : (
          <Card>
            <SetupWizard
              initial={p?.answers ?? emptyAnswers(g.firstName ?? '')}
              initialUrgency={p?.urgency_answers ?? emptyUrgency()}
              current={p}
              startStep={wizard}
              onDone={() => setWizard(null)}
            />
          </Card>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Goals"
        subtitle={`${money(p.answers.incomeGoal, false)} net this year · plan version ${p.version}, since ${date(p.effective_from)}`}
        actions={
          <Button variant="primary" onClick={() => setWizard(3)}>
            Edit plan
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setParams({ tab: t.key })}
            className={cx('rounded-md px-3 py-1.5 text-sm font-medium', tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900')}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'setup' && <SetupTab onEdit={setWizard} />}
      {tab === 'goals' && <GoalsTab />}
      {tab === 'metrics' && <MetricsTab />}
      {tab === 'coach' && <CoachTab />}
    </div>
  );
}

function SetupTab({ onEdit }: { onEdit: (step: number) => void }) {
  const { data: g } = useGoals();
  const a = g!.profile!.answers;
  const rows: [string, string][] = [
    ['Name', a.firstName || '—'],
    ['Experience', EXPERIENCE_LABEL[a.experience]],
    ['Roles', a.roles.map((r) => ROLE_TEMPLATES[r].label).join(', ')],
    ['Property types', a.propertyTypes.join(', ')],
    ['Market', [a.city, a.territory].filter(Boolean).join(' · ')],
    ['Team or solo', a.team === 'solo' ? 'Solo' : 'Team'],
    ['Splits', `${a.houseSplitPct}% after the house, ${a.coBrokerSplitPct}% after co-broker/team`],
    ['Working weeks', String(a.workingWeeks)],
    ['Prospecting time', a.prospectingHours == null ? '—' : `${a.prospectingHours} h/week at ${a.callsPerHour} calls/hour`],
    ['Channels', a.channels.join(', ') || '—'],
  ];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Your answers" actions={<Button size="sm" variant="ghost" onClick={() => onEdit(0)}>Edit</Button>}>
        <dl className="divide-y divide-slate-100 text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="flex gap-3 py-1.5">
              <dt className="w-36 shrink-0 text-slate-500">{k}</dt>
              <dd className="text-slate-800">{v}</dd>
            </div>
          ))}
        </dl>
      </Card>
      <Card title="Private" actions={<Button size="sm" variant="ghost" onClick={() => onEdit(2)}>Edit</Button>}>
        <p className="text-sm text-slate-600">
          Urgency: <b>{g!.profile!.urgency_override ?? g!.profile!.urgency_tier}</b>
          {g!.profile!.urgency_override ? ' (your override)' : ' (from your answers)'} · Months to meaningful income: <b>{g!.profile!.horizon_months}</b>
        </p>
        <p className="mt-2 text-xs text-slate-500">Your financial-support answers are visible only to you.</p>
      </Card>
    </div>
  );
}

function GoalsTab() {
  const { data: g } = useGoals();
  const plan = g!.plan!;
  const metrics: Metric[] = ['revenue', 'calls', 'meetings', 'bovs', 'engagements', 'listings', 'under_contract', 'deals_done'];
  const weeks = g!.profile!.answers.workingWeeks;
  return (
    <div className="space-y-4">
      <Card title="Targets">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="py-1 font-medium">Metric</th>
              <th className="text-right font-medium">Year</th>
              <th className="text-right font-medium">Month</th>
              <th className="text-right font-medium">Week</th>
              <th className="text-right font-medium">Day</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {metrics
              .filter((m) => plan.annual[m] > 0)
              .map((m) => {
                const f = (n: number) => (m === 'revenue' ? money(n, false) : fmt(n));
                return (
                  <tr key={m}>
                    <td className="py-1.5 font-medium text-slate-700">{METRIC_LABEL[m]}</td>
                    <td className="text-right tabular-nums">{f(plan.annual[m])}</td>
                    <td className="text-right tabular-nums">{f(plan.perMonth[m])}</td>
                    <td className="text-right tabular-nums">{f(plan.perWeek[m])}</td>
                    <td className="text-right tabular-nums">{f(plan.perWeek[m] / 5)}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-slate-500">Over {weeks} working weeks. Revenue counts when received, like the Revenue tab.</p>
      </Card>
      {!plan.realism.ok && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          About {fmt(plan.realism.requiredCallsPerWeek)} calls a week are needed; your prospecting time allows about {fmt(plan.realism.capacityPerWeek ?? 0)}. Consider larger assets, better
          conversion, more hours, or a lower goal.
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        {plan.streams.map((s) => (
          <Card key={s.role} title={s.label}>
            <p className="mb-2 text-sm text-slate-600">
              {Math.round(s.share * 100)}% of the goal = {money(s.incomeTarget, false)} · {money(s.grossFeePerDeal, false)} fee → {money(s.netPerDeal, false)} net per deal
            </p>
            <ul className="text-sm text-slate-700">
              <li>
                {fmt(s.calls)} calls → {fmt(s.meetings)} meetings
              </li>
              {s.steps.map((st) => (
                <li key={st.label}>
                  → {fmt(st.annual)} {st.label.toLowerCase()} <span className="text-xs text-slate-400">({Math.round(st.rate * 100)}%)</span>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </div>
  );
}

function MetricsTab() {
  const { data: g } = useGoals();
  const qc = useQueryClient();
  const p = g!.profile!;
  const today = todayISO();
  const first = g!.milestones[0]?.due_date;
  // Milestone progress counts from the first plan's start.
  const { data: since } = useActuals(g!.goalStart, today, g!.timezone);
  const [adding, setAdding] = useState(false);
  const del = useMutation({
    mutationFn: async (id: string) => unwrap(await supabase.from('goal_milestones').delete().eq('id', id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goals'] }),
  });
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        <Card title="This week">
          <GoalBars period="week" metrics={['calls', 'meetings', 'bovs', 'engagements', 'listings', 'under_contract', 'deals_done']} />
        </Card>
        <Card title="This month">
          <GoalBars period="month" metrics={['revenue', 'calls', 'meetings', 'bovs', 'engagements', 'listings', 'under_contract', 'deals_done']} />
        </Card>
        <Card title="Year to date">
          <GoalBars period="ytd" metrics={['revenue', 'calls', 'meetings', 'bovs', 'engagements', 'listings', 'under_contract', 'deals_done']} />
        </Card>
      </div>
      <Card title="Milestones" actions={<Button size="sm" variant="ghost" onClick={() => setAdding(true)}>+ Add</Button>}>
        {adding && <MilestoneForm profileId={p.id} onDone={() => setAdding(false)} />}
        {g!.milestones.length === 0 ? (
          <Empty>No milestones.</Empty>
        ) : (
          <ul className="divide-y divide-slate-100">
            {g!.milestones.map((m) => {
              const actual = since ? actualFor(m.metric as Metric, since, p.answers.roles) : 0;
              const target = Number(m.target);
              const hit = actual >= target;
              const status = hit ? 'done' : m.due_date < today ? 'missed' : 'open';
              return (
                <li key={m.id} className="flex items-center gap-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-800">{m.label}</div>
                    <div className="text-xs text-slate-500">
                      by {date(m.due_date)} · {m.metric === 'revenue' ? money(actual, false) : fmt(actual)} of {m.metric === 'revenue' ? money(target, false) : fmt(target)}
                      {!m.auto && ' · custom'}
                    </div>
                  </div>
                  <Pill color={status === 'done' ? 'green' : status === 'missed' ? 'red' : 'gray'}>{status}</Pill>
                  <button onClick={() => del.mutate(m.id)} className="rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-600" aria-label="Delete milestone" title="Delete milestone">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {first && <p className="mt-2 text-xs text-slate-500">Progress counts from {date(g!.goalStart)}. Generated from your plan; delete or add your own.</p>}
      </Card>
    </div>
  );
}

function MilestoneForm({ profileId, onDone }: { profileId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [metric, setMetric] = useState<Metric>('meetings');
  const [target, setTarget] = useState('');
  const [due, setDue] = useState('');
  const [label, setLabel] = useState('');
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await supabase.from('goal_milestones').insert({
          profile_id: profileId,
          metric,
          target: Number(target),
          due_date: due,
          label: label.trim() || `${Number(target).toLocaleString()} ${METRIC_NOUN[metric]} by ${date(due)}`,
          auto: false,
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goals'] });
      onDone();
    },
  });
  return (
    <div className="mb-3 space-y-2 rounded-md bg-slate-50 p-3">
      <div className="flex flex-wrap gap-2">
        <Select value={metric} onChange={(e) => setMetric(e.target.value as Metric)} className="!w-40" aria-label="Metric">
          {(Object.keys(METRIC_LABEL) as Metric[]).map((m) => (
            <option key={m} value={m}>
              {METRIC_LABEL[m]}
            </option>
          ))}
        </Select>
        <Input inputMode="numeric" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Target" className="!w-28" />
        <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="!w-40" aria-label="Due" />
      </div>
      <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optional)" />
      <ErrorBox error={save.error} />
      <div className="flex justify-end gap-2">
        <Button size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" disabled={!target || !due} busy={save.isPending} onClick={() => save.mutate()}>
          Add
        </Button>
      </div>
    </div>
  );
}

const LEVELS = [
  { key: 'gentle', label: 'Gentle', blurb: 'Encouraging, low pressure. About one nudge a day plus a Monday recap.' },
  { key: 'firm', label: 'Firm', blurb: 'Direct and businesslike. Up to 3 nudges a day.' },
  { key: 'aggressive', label: 'Aggressive', blurb: 'Blunt and urgent. Up to 5 a day, plus an end-of-day check.' },
  { key: 'overseer', label: 'Overseer', blurb: 'Over the top and theatrical. You’ve been warned (arrives with Phase 2).' },
] as const;

function CoachTab() {
  const { data: g } = useGoals();
  const qc = useQueryClient();
  const userId = useUserId();
  const c = g!.coach;
  const save = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => unwrap(await supabase.from('coach_settings').upsert({ owner_id: userId, ...patch }, { onConflict: 'owner_id' })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goals'] }),
  });
  const difficulty = c?.difficulty ?? 'firm';
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Difficulty">
        <p className="mb-3 text-sm text-slate-500">Saved now; the coach’s messages arrive in the next phase. The morning email stays neutral at every level.</p>
        <div className="space-y-2">
          {LEVELS.map((l) => (
            <label key={l.key} className={cx('flex cursor-pointer items-start gap-3 rounded-md border p-3', difficulty === l.key ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-200')}>
              <input type="radio" name="difficulty" className="mt-1" checked={difficulty === l.key} onChange={() => save.mutate({ difficulty: l.key })} />
              <span>
                <span className="font-medium text-slate-900">{l.label}</span>
                <span className="block text-sm text-slate-500">{l.blurb}</span>
              </span>
            </label>
          ))}
        </div>
      </Card>
      <Card title="Sound, quiet hours and snooze">
        <div className="space-y-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={c?.sound_on ?? true} onChange={(e) => save.mutate({ sound_on: e.target.checked })} /> Sound on
          </label>
          <Field label={`Volume (${Math.round(Number(c?.volume ?? 0.4) * 100)}%)`}>
            <input type="range" min={0} max={1} step={0.05} defaultValue={Number(c?.volume ?? 0.4)} onMouseUp={(e) => save.mutate({ volume: Number((e.target as HTMLInputElement).value) })} onTouchEnd={(e) => save.mutate({ volume: Number((e.target as HTMLInputElement).value) })} className="w-56" />
          </Field>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={c?.ghost_in_the_machine ?? true} onChange={(e) => save.mutate({ ghost_in_the_machine: e.target.checked })} /> Ghost in the Machine (Overseer’s glitches)
          </label>
          <div className="flex flex-wrap gap-3">
            <Field label="Quiet hours from">
              <Input type="time" defaultValue={c?.quiet_start?.slice(0, 5) ?? ''} onBlur={(e) => save.mutate({ quiet_start: e.target.value || null })} className="!w-32" />
            </Field>
            <Field label="to">
              <Input type="time" defaultValue={c?.quiet_end?.slice(0, 5) ?? ''} onBlur={(e) => save.mutate({ quiet_end: e.target.value || null })} className="!w-32" />
            </Field>
          </div>
          <Field label="Snooze the coach until (vacation, illness)">
            <Input type="date" defaultValue={c?.snooze_until ?? ''} onBlur={(e) => save.mutate({ snooze_until: e.target.value || null })} className="!w-44" />
          </Field>
          <ErrorBox error={save.error} />
        </div>
      </Card>
    </div>
  );
}
